import axios from "axios"
import Cookies from "js-cookie";
import logger from "../../../logger";
import pubsub from "@viewlift/pubsub";
import { getStorageItem } from "../../../helpers/storageManager";
import { fireLogout, getToken } from "../../../helpers";

const getNbaConfig = () => {
    if (typeof window === "undefined") {
        return {};
    }
    return window?.app_data?.appcmsMain?.ssoConfig?.nba || {};
}

const NBA_ACCESS_TOKEN_LIFETIME_IN_SECONDS = 1800
const NBA_SESSION_EXPIRED = "nba_session_expired"

const storeNbaTokens = (nbaTokens) => {
    const expiresInSeconds = nbaTokens?.expires_in || NBA_ACCESS_TOKEN_LIFETIME_IN_SECONDS
    localStorage.setItem("nbaAuthConfig", JSON.stringify(nbaTokens))
    Cookies.set('nbaAuthToken', nbaTokens.access_token, {
        expires: expiresInSeconds / (60 * 60 * 24), // js-cookie expects days
        secure: true,
    })
}

const hasNbaSession = () => !!localStorage.getItem("nbaAuthConfig")

const clearNbaSession = () => {
    localStorage.removeItem("nbaAuthConfig")
    localStorage.removeItem("nbaUserInfo")
    localStorage.removeItem("nbaSelectedPlan")
    Cookies.remove('nbaAuthToken')
}

const isNbaSessionExpired = (err) =>
    err?.message === NBA_SESSION_EXPIRED || err?.response?.data?.error === "invalid_grant"


const getNbaAccessToken = async({ redirectOnExpiredSession = true } = {}) => {
    const nbaAuthToken = Cookies.get('nbaAuthToken')
    if(nbaAuthToken) return nbaAuthToken
    try {
        const res = await refreshNbaToken()
        // Prefer the token from the refresh response so callers never get undefined
        // even if the cookie write/read fails (e.g. secure cookie on http).
        return res?.data?.access_token || Cookies.get('nbaAuthToken')
    } catch (err) {
        // nothing left to refresh with, the only way back is signing in at NBA again
        if(redirectOnExpiredSession && isNbaSessionExpired(err)) {
            fireLogout()
        }
        throw err
    }
}

const getStoredNbaUser = () => JSON.parse(localStorage.getItem("nbaUserInfo") || "null")

const getNbaApiHeaders = async() => {
    const { isEnabled } = getNbaConfig()
    if (!isEnabled) return {}

    let userInfo = await getStorageItem("AuthenticationStore")
    let user = userInfo?.user
    let nbaAuthToken = Cookies.get('nbaAuthToken')

    // Cookie gone but VL user still present — refresh via getNbaAccessToken so an
    // expired NBA session clears local state and redirects to NBA login. Catch so
    // identity/subscription callers that await this helper never reject.
    if (user && !nbaAuthToken) {
        try {
            nbaAuthToken = await getNbaAccessToken()
        } catch (err) {
            logger(`NBA token refresh for API headers failed: ${err}`, "error")
            return {}
        }
    }

    // Never attach a leftover cookie when VL user is gone. Also scrub it so a
    // later anonymous visitor on a shared browser cannot read/reuse it for the
    // remaining ~30m cookie lifetime (covers older clearStorageItem paths that
    // only removed nbaAuthConfig / nbaUserInfo).
    // if (!user) {
    //     if (nbaAuthToken) clearNbaSession()
    //     return {}
    // }
    if (!nbaAuthToken) return {}
    return { 'x-nba-token': nbaAuthToken }
}

const generateStateValue = () => {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);

    const uniqueKey =  Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
    localStorage.setItem("nbaAuthStateKey", uniqueKey)
    return uniqueKey
}

// Redirect To NBA Login Page
const createNbaAuthSession = () => {
    const { isEnabled, oidcBaseUrl, webClientId, webOidcRedirectUrl } = getNbaConfig();
    if(!isEnabled){
        throw Error("NBA Auth is not enabled via CMS")
    }
    pubsub.publish('apploading', true);
    const stateKey = generateStateValue()
    const endpoint = `${oidcBaseUrl}/oidc/authorize`
    const formData = new FormData()
    formData.append("scope", "openid profile email");
    formData.append("response_type", "code");
    formData.append("client_id", webClientId);
    formData.append("redirect_uri", webOidcRedirectUrl);
    formData.append("state", stateKey);
    const params = new URLSearchParams(formData)
    // redirect to NBA Login Page
    window.location.href = `${endpoint}?${params}`
    pubsub.publish('apploading', false);
}

//  Exchange the Authorization Code for Tokens
const createNbaToken= async(code) => {
    if(!code){
        throw Error('code not found')
    }
    const { webClientId, webOidcRedirectUrl } = getNbaConfig();
    let baseUrl = window?.app_data?.appcmsMain?.apiConfig?.apiBaseUrl		
     const endpoint = `${baseUrl}/v4/identity/nba/token`
     const token = await getToken();
    return axios({
        method: 'post',
        url: endpoint,
        headers: { "x-api-key": window?.xApiKey, "Authorization": token },
        data: { grant_type: "authorization_code", client_id: webClientId, redirect_uri: webOidcRedirectUrl, code: code}
    })
}

// Refresh Token logic (single-flight: concurrent callers share one request).
// Assign refreshNbaTokenPromise synchronously before any await so concurrent
// callers cannot both miss the lock and burn a one-time-use refresh token.
let refreshNbaTokenPromise = null
const refreshNbaToken = () => {
    if (refreshNbaTokenPromise) return refreshNbaTokenPromise
    const refreshToken = JSON.parse(localStorage.getItem("nbaAuthConfig"))?.refresh_token
    if(!refreshToken) return Promise.reject(Error(NBA_SESSION_EXPIRED))

    const baseUrl = window?.app_data?.appcmsMain?.apiConfig?.apiBaseUrl
    const { webClientId, webOidcRedirectUrl } = getNbaConfig()
    const endpoint = `${baseUrl}/v4/identity/nba/token`

    // Do not logout here — callers decide via getNbaAccessToken({ redirectOnExpiredSession }).
    // Transient network/proxy errors must reject without signing the user out.
    refreshNbaTokenPromise = getToken().then((token) => {
        return axios({
            method: 'post',
            url: endpoint,
            headers: { "x-api-key": window?.xApiKey, "Authorization": token },
            data: { grant_type: "refresh_token", client_id: webClientId, refresh_token: refreshToken, redirect_uri: webOidcRedirectUrl}
        })
    }).then((res) => {
        storeNbaTokens(res.data)
        return res
    }).finally(() => {
        refreshNbaTokenPromise = null
    })
    return refreshNbaTokenPromise
}

// Get User Details
const getNbaUserInfo= async() => {
    let baseUrl = window?.app_data?.appcmsMain?.apiConfig?.apiBaseUrl	
    const endpoint = `${baseUrl}/v4/identity/nba/userinfo`
    const nbaAuthToken = await getNbaAccessToken()
    const token = await getToken();
    return axios({
        method: 'get',
        url: endpoint,
        headers: { 
            "Authorization": token,
            "x-nba-token": nbaAuthToken,
            "x-api-key": window?.xApiKey,
          },
    }).then((res) => {
        localStorage.setItem("nbaUserInfo", JSON.stringify(res.data))
        return res
    })
}

// revoke takes the token in the body, not as a bearer header
const nbaLogout = async() => {
    let baseUrl = window?.app_data?.appcmsMain?.apiConfig?.apiBaseUrl	
    const endpoint = `${baseUrl}/v4/identity/nba/revoke`
    const token = await getToken();
    const nbaAuthToken = await getNbaAccessToken({ redirectOnExpiredSession: false })
    return axios({
        method: 'post',
        url: endpoint,
        headers: { "Authorization": token, "x-api-key": window?.xApiKey, 'Content-Type': "application/json" },
        data: { token: nbaAuthToken }
    })
}

const nbaSignOut = async() => {
    try {
        await nbaLogout()
    } catch (err) {
        logger(`NBA revoke failed: ${err}`, "error")
    }
    clearNbaSession()
}

export {createNbaAuthSession, createNbaToken, refreshNbaToken, getNbaUserInfo, storeNbaTokens, hasNbaSession, clearNbaSession, getStoredNbaUser, nbaSignOut, isNbaSessionExpired, getNbaAccessToken, getNbaApiHeaders, getNbaConfig}